import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ClientPath, castClientNameToClientPathEnum } from '@shared/models/client-path.enum';
import { ThemeService } from '@shared/services/theme.service';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import {
  ISiteSettings,
  PlatformProviderType,
  PlatformRootRoute,
} from '@shared/shared/interfaces/site-settings.interfaces';
import { SiteConfigService } from '@shared/shared/services/site-config.service';
import { OFFERS_PROMO_BANNER_CLOSED, USER_SELECTED_SITE, WELCOME_VIDEO_SHOWN } from 'app.config';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { ClientWithDesignAdminCSS, ClientWithExternalSiteCSS } from '../constants/site-config-client-exceptions';

const defaultSettings: ISiteSettings = {
  id: null,
  advance_booking_window: '24h',
  switches: {
    accept_bin_for_eligibility: false,
    access_to_HTR: false,
    access_to_webchat: false,
    authentication_coutts_sso: false,
    authentication_sso: false,
    auto_invite_new_member: false,
    checked_bags_tool_tip: false,
    client_restrict_contacts_my_profile: false,
    client_restrict_contacts_request_and_booking_summary: false,
    concierge_briefing: false,
    contact_details: false,
    custom_footer_design: false,
    delegate_members: false,
    details_address: false,
    details_member_profile: false,
    details_phone_number: false,
    dining_auto_translate: false,
    dining_booking_recaptcha: false,
    dining_landing_show_local_content_only: false,
    dining_module: false,
    dining_oss_phase_1: false,
    dining_oss_phase_2: false,
    dining_request_recaptcha: false,
    dining_third_party_integrations: false,
    display_installments_contact_banner: false,
    emergency_banner_entertainment: false,
    emergency_banner_homepage: false,
    emergency_banner_travel: false,
    enable_centurion_benefits: false,
    enable_centurion_card_design_selector: false,
    enable_cpf_check: false,
    enable_member_acquisition_homepage: false,
    enable_mtls_priceless_planet_widget: false,
    enable_pay_with_points: false,
    enable_pwp_hotels_path: false,
    enable_recaptchas: false,
    enable_referral_rock_script: false,
    enable_eligibility_recaptcha: false,
    enable_registration_recaptcha: false,
    enable_email_change_recaptcha: false,
    enable_adyen_instalments: false,
    entertainment_booking_recaptcha: false,
    entertainment_module: false,
    entertainment_request_recaptcha: false,
    entertainment_search_show_local_content_only: false,
    entertainment_redesign_phase_1: false,
    entertainment_redesign_phase_1_1: false,
    entertainment_redesign_phase_2: false,
    entertainment_redesign_phase_3: false,
    inspiration_module: false,
    is_sharia_compliant: false,
    limousine_v2: false,
    member_activity_covid_support_points: false,
    member_benefit_request_recaptcha: false,
    member_benefits_module: false,
    member_events_booking_recaptcha: false,
    member_events_module: false,
    member_status_endpoint: false,
    newsletter_notifications: false,
    partners_module: false,
    payment_cards: false,
    payment_ingenico: false,
    preferences_dining: false,
    preferences_artist: false,
    preferences_member_benefits: false,
    preferences_interests: false,
    preferences_page: false,
    preferences_travel: false,
    product_dropdown: false,
    require_address_on_registration: false,
    show_phone_number_on_registration_form: false,
    require_phone_number_as_mandatory: false,
    show_date_of_birth_on_registration_form: false,
    require_date_of_birth_on_registration_form: false,
    request_management_feature: false,
    requests_module: false,
    self_registration: false,
    send_member_benefit_confirmation_emails: false,
    show_address_on_registration: false,
    show_chat_phone_number: false,
    show_country_language_selector_page: false,
    pre_login_home_page: false,
    pre_login_benefits_page: false,
    pre_login_inspiration_page: false,
    show_genesys_web_messenger: false,
    show_hotel_details_page_message: false,
    show_marketing_opt_in_prompt: false,
    show_registration_result_in_popup: false,
    show_tour: false,
    sitata_travel_search_widget: false,
    stored_cards: false,
    travel_car_hire_module: false,
    travel_covid_support_points: false,
    travel_fare_families: false,
    travel_flights_expedited_immigration_request: false,
    travel_flights_limousine_request: false,
    travel_flights_local_carriers: false,
    travel_flights_module: false,
    travel_hotels_module: false,
    travel_module: false,
    travellers_frequent_flyer: false,
    travellers_hotel_loyalty: false,
    travellers: false,
    use_car_hire_payment_instalments: false,
    travel_hotels_rooms_price_per_night: false,
    preferences_follow_events_and_entertainment: false,
    travel_activities_module: false,
    hotel_search_results_redesign: false,
    change_marketing_consent_on_platform: false,
    enable_wootric_online_survey: false,
  },
  welcome_video_url: undefined,
  contact_numbers: {
    dining: '',
    entertainment: '',
    general: '',
    member_benefits: '',
    member_events: '',
    support: '',
    travel: '',
  },
  currency: '',
  client: { name: '' },
  domain: '',
  genesys_widget_key: '',
  genesys_web_messenger_deployment_id: '',
  global_travel_value_proposition: '',
  language: '',
  name: '',
  main_country: '',
  scripts: '',
  workgroup: '',
  fe_configuration: {
    segment_script_id: 'c2JmWuF3eHcJjv2IfyLeejrG3oiUtsAg',
    show_platform_provider_nav: false,
    show_platform_provider_footer: false,
    platform_provider_type: PlatformProviderType.PROVIDER,
    hide_links_to_app_features: false,
    ghostry_cookie_consent_script_active: false,
    single_feature_brand: false,
    root_route: PlatformRootRoute.LANDING,
    meta_description: '',
    meta_keywords: '',
    name: 'Ten Private',
    path: 'ten',
    show_homepage_video: false,
    self_registration: false,
  },
  country_code: 'GB',
  country_code_alpha3: 'USA',
  authentication_provider: {
    provider: 'ten',
    login_url: null,
  },
  use_payment_service: false,
  product_group_ordering: [],
  hotel_ordering_level: undefined,
  eligibility_pan_length: undefined,
  eligibilities_and_selfregs: undefined,
  covid_entry_point_display_format: '',
  travel_services: [],
  mtls_priceless_planet_script: undefined,
  header_external_portal_hyperlink: '',
  cookie_setting_id: '',
  documentation_url: {
    title: '',
    link: '',
  },
  country_latitude: '',
  country_longitude: '',
};

@Injectable({
  providedIn: 'root',
})
export class SiteSettingsService {
  public settings: ISiteSettings = defaultSettings;

  public fetchingSettings = false;
  public settingsRetrieved = false;

  public initialSubject = new BehaviorSubject(defaultSettings);

  constructor(
    private http: HttpClient,
    private themeService: ThemeService,
    private cookieService: CookieService,
    private siteConfigService: SiteConfigService
  ) {}

  public fetchSiteSettings(): Observable<ISiteSettings> {
    const siteIdInUrl = this.getSiteIdFromURL();

    if (siteIdInUrl) {
      this.setSiteIdFromURL();
    }

    if (!this.fetchingSettings && !this.settingsRetrieved) {
      this.awaitFetchSettings().subscribe();
    }

    return this.initialSubject;
  }

  public loadSiteSettings(): void {
    this.awaitFetchSettings().subscribe();
  }

  public canSkipSelectorPageWithSiteId() {
    const allowedSites = ['mastercard', 'hsbc'];
    let canSkip = false;

    allowedSites.forEach((site) => {
      if (canSkip) {
        return canSkip;
      }
      canSkip = window.location.origin.includes(site);
    });

    return canSkip;
  }
  private awaitFetchSettings(): Observable<ISiteSettings> {
    this.fetchingSettings = true;

    return this.http.get(ApiEndpoint.SITE_CONFIGURATION).pipe(
      tap((response: ISiteSettings) => {
        // If there are discrepancies about the site id send the user back to the selector page

        if (response.id === null || response.id === undefined || !this.isSiteIdMatchesSite(response)) {
          this.selectorPageRedirect();
          return;
        }

        this.settings = {
          ...this.settings,
          ...response,
          advance_booking_window: this.calculateBookingAdvanceWindow(response.advance_booking_window),
        };

        this.themeService.setUpTheme(this.settings);

        this.fetchingSettings = false;
        this.settingsRetrieved = true;

        this.initialSubject.next(this.settings);
      }),
      catchError((e) => {
        this.fetchingSettings = false;
        this.settingsRetrieved = false;
        //status 0 = CORS issue but not sure as used a plugin to overcome CORS and the issue exists.
        // every other status error will fall through and correctly reset the page just a hack for FF then.
        if (e.status !== 0) {
          this.selectorPageRedirect();
        }
        return EMPTY;
      })
    );
  }

  private calculateBookingAdvanceWindow(advanceBookingWindow: string): string {
    const regexp = new RegExp(`^(\\d+)(h)$`);
    const value = advanceBookingWindow.match(regexp)?.[1] || 24;
    return (+value / 24).toString();
  }

  public selectorPageRedirect() {
    localStorage.removeItem(USER_SELECTED_SITE);
    this.cookieService.delete('selectedSite');

    window.location.href = `${window.location.origin}/selector`;
  }

  public getSiteCss(): Promise<string> {
    return new Promise((resolve) => {
      const clientPath: ClientPath = castClientNameToClientPathEnum(this.settings.fe_configuration.path);
      if (!ClientWithExternalSiteCSS.includes(clientPath)) resolve(null);

      this.http.get(ApiEndpoint.SITE_CSS_SETTINGS, { responseType: 'text' }).subscribe({
        next: (response) => resolve(response),
        error: () => resolve(undefined),
      });
    });
  }

  public getDesignAdminSiteCss(): Promise<string> {
    return new Promise((resolve) => {
      const clientPath: ClientPath = castClientNameToClientPathEnum(this.settings.fe_configuration.path);
      if (!ClientWithDesignAdminCSS.includes(clientPath)) {
        resolve(null);
      } else {
        const url = `${ApiEndpoint.SITE_ADMIN_CSS_SETTINGS}${clientPath}.css`;
        this.http
          .get(url, {
            responseType: 'text',
            headers: new HttpHeaders().set('Accept', 'application/json, text/plain, */*'),
          })
          .pipe(catchError(() => of('')))
          .subscribe((response) => {
            resolve(response);
          });
      }
    });
  }

  public getSiteIdFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const siteId: string | number = urlParams.get('site_id') || urlParams.get('selectedSite');

    return siteId ? +encodeURI(siteId) : null;
  }

  public setSiteIdFromURL() {
    let siteId: string | number = this.getSiteIdFromURL();

    if (siteId) {
      siteId = +siteId;
      if (siteId > 0 && this.isValidURLPathForSiteId()) {
        this.syncSiteId(siteId);
      }
    }
  }

  private readonly brandPathExceptions = [
    {
      originalBrandPath: ClientPath.MASTERCARD_RAK,
      replacementPrandPath: ClientPath.MASTERCARD_RAK_BANK,
    },
    {
      originalBrandPath: ClientPath.MASTERCARD_HNW,
      replacementPrandPath: ClientPath.MASTERCARD,
    },
  ];

  public replaceBrandPath(brandPath: string): string | null {
    const path = this.brandPathExceptions.find((pathObject) => pathObject.originalBrandPath === brandPath);
    return path ? path.replacementPrandPath : brandPath;
  }

  // Use for sites where it's one url but multiple clients, example here mastercard high networth
  // has a different UI but the UI is still Mastercard, Site selector defaults to the parent path
  // but should be in this case mastercard-hnw
  //
  // cant use FE config for this because until the site is loaded it's still mastercard, and when
  // https://tenproduct.atlassian.net/browse/TPD-19902
  public brandPathOverride(brandPath: string, countryCode: string) {
    return this.siteConfigService.getComponentData('brandPathOverride')[countryCode] ?? brandPath;
  }

  public syncSiteId(siteId: number | string) {
    if (!siteId) {
      this.clearLocalStorageSiteId();
      this.clearCookieSiteId();
    } else {
      this.setLocalStorageSiteId(siteId.toString());
      this.setCookieSiteId(siteId.toString());
    }
  }

  private setLocalStorageSiteId(siteId: string) {
    localStorage.setItem(USER_SELECTED_SITE, siteId);
  }

  private setCookieSiteId(siteId: string) {
    this.cookieService.set('selectedSite', siteId, {
      expires: 30,
      path: '/',
      sameSite: 'None',
      secure: true,
    });
  }

  private clearLocalStorageSiteId() {
    localStorage.removeItem(USER_SELECTED_SITE);
  }

  private clearCookieSiteId() {
    this.cookieService.delete('selectedSite', '/');
  }

  public invalidateSettings() {
    this.settingsRetrieved = false;

    this.initialSubject.next(defaultSettings);
    // do not call getInfo here or you can end up in an infinite loop
  }

  public isValidURLPathForSiteId() {
    return ['/registration', '/activate', '/user-ineligible', '/product'].some((path: string) =>
      window.location.href.includes(path)
    );
  }

  public isSiteIdMatchesSite(siteSettings: ISiteSettings): boolean {
    // If the site id doesn't match the url params these two urls will differ
    // and an easy way to check if the "manually" set siteId is correct for that site
    return siteSettings.main_country
      .replace(/^https?:\/\//, '')
      .includes(siteSettings.domain.replace(/^https?:\/\//, ''));
  }

  public clearLocal() {
    // TODO refactor this once VISA-LAC issue is solved
    const offersPromoBannerClosed = localStorage.getItem(OFFERS_PROMO_BANNER_CLOSED);
    const welcomeVideoShown = localStorage.getItem(WELCOME_VIDEO_SHOWN);

    localStorage.clear();
    sessionStorage.clear();

    // TODO refactor this once VISA-LAC issue is solved
    if (offersPromoBannerClosed) {
      localStorage.setItem(OFFERS_PROMO_BANNER_CLOSED, offersPromoBannerClosed);
    }
    if (welcomeVideoShown) {
      localStorage.setItem(WELCOME_VIDEO_SHOWN, welcomeVideoShown);
    }

    const cookies = this.cookieService.getAll();
    for (const key in cookies) {
      if (Object.prototype.hasOwnProperty.call(cookies, key)) {
        // one trust cookie to keep after logging out
        if (key !== 'OptanonConsent' && key !== 'OptanonAlertBoxClosed' && key !== 'entertainmentTicketForm') {
          this.cookieService.delete(key, '/');
          this.cookieService.delete(key, '/', window.location.hostname);
        }
      }
    }
  }

  public setOneTrustManageCookieBtnVisibility() {
    if (document.getElementById('ot-sdk-btn')) {
      document.getElementById('ot-sdk-btn').style.display = 'block';
    }
  }

  // temporary helper for new dining phase 1 integration
  public isNewDining(): Observable<boolean> {
    return this.fetchSiteSettings().pipe(
      take(1),
      map((settings) => settings.switches.dining_oss_phase_1 && settings.switches.dining_third_party_integrations)
    );
  }

  // temporary helper for new dining phase 2 integration
  public isNewDining2(): Observable<boolean> {
    return this.fetchSiteSettings().pipe(
      take(1),
      map((settings) => settings.switches.dining_oss_phase_2 && settings.switches.dining_third_party_integrations)
    );
  }
}
